import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/categories";
import ProductCard from "../components/ProductCard";
import { askLogin } from "../components/FavoriteButton";
import { useAuth } from "../context/AuthContext";
import { useFavorites } from "../context/FavoritesContext";
import { Search, Heart } from "lucide-react";

const SORTERS = {
  newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
  price_asc: (a, b) => a.price - b.price,
  price_desc: (a, b) => b.price - a.price,
};

export default function Shop() {
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [category, setCategory] = useState("");
  const [onlyFav, setOnlyFav] = useState(false);
  const { user } = useAuth();
  const { favorites } = useFavorites();
  const navigate = useNavigate();

  useEffect(() => {
    const reqParams = {};
    if (q) reqParams.q = q;
    if (category) reqParams.category = category;
    let stale = false;
    api.get("/products", { params: reqParams }).then((r) => { if (!stale) setProducts(r.data); });
    return () => { stale = true; };
  }, [q, category]);

  const toggleFav = () => (user ? setOnlyFav(!onlyFav) : askLogin(navigate));
  const favMode = onlyFav && !!user;
  const sorted = (products || []).filter((p) => !favMode || favorites.includes(p.id)).sort(SORTERS[sort]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-testid="shop-page">
      <div className="mb-12">
        <p className="font-accent text-3xl text-terracotta -rotate-2 mb-2">La Boutique</p>
        <h1 className="font-serif-display text-5xl lg:text-6xl">Toutes nos créations</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="category-filters">
        {["", ...CATEGORIES].map((c) => (
          <button
            key={c || "all"}
            onClick={() => setCategory(c)}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.14em] transition-colors ${
              category === c ? "bg-terracotta text-white" : "bg-white border border-[#E5E1D8] text-[#1F1C18] hover:border-terracotta"
            }`}
            data-testid={`filter-${c ? c.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-") : "all"}`}
          >
            {c || "Tout"}
          </button>
        ))}
        <button
          onClick={toggleFav}
          aria-pressed={favMode}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs uppercase tracking-[0.14em] transition-colors ${
            favMode ? "bg-[#1F1812] text-white" : "bg-white border border-[#D7334F]/50 text-terracotta hover:bg-[#D7334F]/10"
          }`}
          data-testid="filter-favorites"
        >
          <Heart size={14} className={favMode ? "fill-[#D7334F] text-[#D7334F]" : ""} />
          Favoris{user && favorites.length > 0 ? ` (${favorites.length})` : ""}
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-10 pb-6 border-b border-[#E5E1D8]">
        <label className="flex items-center gap-2 text-sm text-[#6B655C]">
          Trier par
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-4 py-2.5 rounded-full border border-[#E5E1D8] bg-white text-sm text-[#1F1C18] focus:outline-none focus:border-terracotta cursor-pointer"
            data-testid="sort-select"
          >
            <option value="newest">Nouveautés</option>
            <option value="price_asc">Prix croissant</option>
            <option value="price_desc">Prix décroissant</option>
          </select>
        </label>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B655C]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher..."
            className="pl-9 pr-4 py-2.5 rounded-full border border-[#E5E1D8] bg-white text-sm focus:outline-none focus:border-terracotta w-64"
            data-testid="search-input"
          />
        </div>
      </div>

      {products === null ? (
        <p className="text-center text-[#6B655C] py-20" data-testid="shop-loading">Chargement...</p>
      ) : sorted.length === 0 ? (
        <p className="text-center text-[#6B655C] py-20" data-testid="shop-empty">
          {favMode ? "Vous n'avez pas encore de favoris ici. Cliquez sur le cœur d'une pièce pour la garder !" : "Aucun produit trouvé."}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
          {sorted.map((p, i) => (
            <ProductCard key={p.id} product={p} idx={i} />
          ))}
        </div>
      )}
    </div>
  );
}
